'use client';

import { useCareToday } from '@/hooks/useCareToday';
import { useModules } from '@/contexts/module-context';
import { useGovernmentIntegrations } from '@/contexts/government-integration-context';
import { useAuthStore } from '@/stores/auth-store';
import { useEffect, useState } from 'react';
import type { HelpTenantFilterContext } from '@/lib/help';
import {
  resolveHelpOrgTypeForProgram,
  resolveHelpProgram,
  resolveHelpTenantContext,
} from '@/lib/help/tenant-context';
import { fetchTenantConfig } from '@/lib/supabase/tenant-shell-queries';
import type { ModuleOrgType } from '@/lib/billing';
import { useOrgLabels } from '@/contexts/org-labels-context';
import type { ProviderType } from '@/lib/navigation/nav-v2';
import {
  useStationPinHelpContext,
  useScopedHelpFeatures,
} from '@/contexts/station-pin-help-context';

export function useHelpTenantContext(
  tenantId: string | null | undefined,
  providerTypeOverride?: ProviderType,
) {
  const integrations = useGovernmentIntegrations();
  const { payrollRebuildOn } = useModules();
  const careToday = useCareToday();
  const role = useAuthStore((state) => state.user?.role ?? null);
  const actorId = useAuthStore((state) => state.user?.id ?? null);
  const stationPinManagerRevoke = useStationPinHelpContext(tenantId, actorId);
  const helpFeatures = useScopedHelpFeatures(tenantId, actorId);
  const { providerType: contextProviderType, activeProgramFamily } = useOrgLabels();
  const providerType = providerTypeOverride ?? contextProviderType;
  const program = resolveHelpProgram(activeProgramFamily) ?? resolveHelpProgram(providerType);
  const [enabledModules, setEnabledModules] = useState<string[] | null>(null);
  const [orgType, setOrgType] = useState<ModuleOrgType | null>(null);
  const [tenantContext, setTenantContext] = useState<HelpTenantFilterContext>({ tenantId: null });
  const [loading, setLoading] = useState(Boolean(tenantId));
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!tenantId) {
      setLoading(false);
      setError(true);
      setEnabledModules([]);
      setOrgType(null);
      setTenantContext({ tenantId: null });
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    fetchTenantConfig(tenantId)
      .then((config) => {
        if (cancelled) return;
        if (!config) {
          setError(true);
          setEnabledModules([]);
          setOrgType(null);
          setTenantContext({ tenantId, isDemo: null, orgType: null, providerType: 'UNKNOWN' });
          return;
        }
        const resolved = resolveHelpTenantContext(tenantId, config);
        const programOrgType = resolveHelpOrgTypeForProgram(providerType);
        setEnabledModules(resolved.enabledModules);
        setOrgType(programOrgType);
        setTenantContext({
          ...resolved.tenantContext,
          orgType: programOrgType,
          providerType,
          program,
        });
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setEnabledModules([]);
        setOrgType(null);
        setTenantContext({ tenantId, isDemo: null, orgType: null, providerType: 'UNKNOWN' });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [activeProgramFamily, providerType, tenantId]);

  const scopedTenantContext: HelpTenantFilterContext = {
    ...tenantContext,
    payrollRebuildOn,
    tenantFlags: { care_today_v1: careToday.enabled },
    governmentIntegrations: integrations,
    role,
    ...(stationPinManagerRevoke ? { stationPinManagerRevoke: true } : {}),
    ...(helpFeatures.stationPinSelfService ? { stationPinSelfService: true } : {}),
    ...(helpFeatures.agencyAccess ? { agencyAccess: true } : {}),
  };

  return {
    enabledModules,
    orgType,
    tenantContext: scopedTenantContext,
    loading,
    error,
  };
}
